import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import pluralize from 'pluralize';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import FlipIcon from '@material-ui/icons/Flip';
import AppActions from '../../actions/app-actions';
import { showError } from '../../helpers';
import { useTableState, useTimeout } from '../../custom-hooks';
import Loader from '../common/loader';
import CTable from '../common/ctable';
import { columns } from './tenant-table-def';
import TenantDialog from './tenant-dialog';

const timeoutInMilliSec = 30 * 1000;
const columnDefinition = columns();

const TenantManagement = () => {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState({ open: false });
  const { colDef, selectedRows, onRowSelect, onAllRowSelect, onSelectedRowsReset, onFilterChange, onColumnChange } = useTableState(columnDefinition);
  const [startTimer] = useTimeout(fetchDetails, timeoutInMilliSec);

  const leftSideToolbarActions = useCallback(() => {
    if (selectedRows.length) {
      return (
        <>
          <Tooltip title='Delete'>
            <IconButton color='primary' onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Change Status'>
            <IconButton color='primary' onClick={onEditTenant}>
              <FlipIcon />
            </IconButton>
          </Tooltip>
        </>
      );
    } else {
      return null;
    }
  }, [selectedRows, list]);

  function onCreteTenant () {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (name, status) => {
      const mStatus = status === 'Active' ? 'Active' : 'Suspended';
      AppActions.createTenant({ name, status: mStatus })
        .then(res => {
          ReactDOM.unstable_batchedUpdates(() => {
            setList([...list, res.body]);
            setDlg(() => ({ open: false }));
            AppActions.setSnackbar(`A new tenant "${res.body.name}" created successfully`);
          });
        })
        .catch(err => {
          ReactDOM.unstable_batchedUpdates(() => {
            setDlg(() => ({ open: false }));
            showError(err);
          });
        });
    };
    setDlg(() => ({ open: true, type: 'create', handleCancel, handleOk }));
  }

  function onEditTenant () {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (status, sptsStatus, proxyStatus, one2mStatus) => {
      const mSptsStatus = sptsStatus === 'Enabled';
      const mProxyStatus = proxyStatus === 'Enabled';
      const mOne2MStatus = one2mStatus === 'Enabled';
      const mStatus = status === 'Active' ? 'Active' : 'Suspended';
      const editStatusPromises = selectedRows.map(sr => AppActions.editTenant(sr.id, { status: mStatus }));
      const editSptsStatusPromises = selectedRows.map(sr => AppActions.editTenantFeatures(sr.id, [{type: 'spts', status: mSptsStatus}]));
      const editProxyStatusPromises = selectedRows.map(sr => AppActions.updateProxyStatus(sr.id, [{type: 'http_proxy', status: mProxyStatus},
        {type: 'one2m', status: mOne2MStatus}]));
      Promise.all([editStatusPromises, editSptsStatusPromises, editProxyStatusPromises])
        .then(() => {
          const selCount = selectedRows.length;
          const newList = list.map(item => {
            const fd = selectedRows.find(sr => item.id === sr.id);
            if(fd) {
              item.status = mStatus;
              item.sptsStatus = sptsStatus;
              item.proxyStatus = proxyStatus;
              item.one2mStatus = one2mStatus;

              item.tenant_features
                ?.filter(feature => feature.type === 'http_proxy')
                ?.forEach(feature => feature.status = mProxyStatus);

              item.tenant_features
                ?.filter(feature => feature.type === 'one2m')
                ?.forEach(feature => feature.status = mOne2MStatus);

              item.post_process
                ?.filter(process => process.type === 'spts')
                ?.forEach(process => process.status = mSptsStatus);
            }
            return item;
          });
          ReactDOM.unstable_batchedUpdates(() => {
            setList(newList);
            onSelectedRowsReset();
            setDlg(() => ({ open: false }));
            AppActions.setSnackbar(`Selected ${pluralize('Tenant', selCount)} ${status === 'Active' ? 'Activated' : 'Suspended'} successfully`);
          })
        })
        .catch(err => {
          ReactDOM.unstable_batchedUpdates(() => {
            setDlg(() => ({ open: false }));
            showError(err, `Can't edit selected Tenant`);
          });
        });
    };
    setDlg(() => ({ open: true, type: 'edit', handleOk, handleCancel }));
  }

  function onDelete () {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      const delPromises = selectedRows.map(sr => AppActions.deleteTenant(sr.id));
      Promise.all(delPromises)
        .then(() => {
          const newList = list.filter(fId => (selectedRows.findIndex(srId => fId.id === srId.id)) === -1);
          ReactDOM.unstable_batchedUpdates(() => {
            setList(newList);
            onSelectedRowsReset();
            setDlg(() => ({ open: false }));
            AppActions.setSnackbar(`Selected ${pluralize('tenant', selectedRows.length)} deleted successfully`);
          });
        })
        .catch(err => {
          ReactDOM.unstable_batchedUpdates(() => {
            setDlg(() => ({ open: false }));
            showError(err, `Couldn't delete selected tenant`);
          });
        });
    };
    setDlg({ open: true, type: 'remove', handleOk, handleCancel, count: selectedRows.length });
  }

  const loader = () => (<div><Loader show={true} /></div>);

  const bodyContent = () => {
    if (loading)
      return loader();
    else {
      return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container justify='flex-end'>
              <Grid item>
                <Button variant='contained' color='primary' onClick={onCreteTenant}>Create New Tenant</Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <CTable
              colDef={colDef}
              data={list}
              selectedRows={selectedRows}
              leftSideToolbarActions={leftSideToolbarActions}
              onRowSelect={onRowSelect}
              onFilterChange={onFilterChange}
              onColumnChange={onColumnChange}
              onAllRowSelect={onAllRowSelect}
            />
          </Grid>
        </Grid>
        {dlg.open ? <TenantDialog { ...dlg} selectedRows={selectedRows} /> : null}
      </>
      );
    }
  };

  function setSptsStatus(list) {
    list?.forEach(item => {
      item.post_process
        ?.filter(process => process.type === 'spts')
        .forEach(process => item.sptsStatus = process.status ? 'Enabled' : 'Disabled');
    });
  }

  function setProxyStatus(list) {
    list.forEach(item => {
      const tenantFeatures = item.tenant_features;
      if(tenantFeatures !== undefined && tenantFeatures !== null && tenantFeatures.length > 0)
        for (let i=0; i<tenantFeatures.length; i++) {
          if (tenantFeatures[i].type == 'http_proxy') {
            item.proxyStatus = tenantFeatures[i].status ? 'Enabled' : 'Disabled';
            break;
          }
        }
    });
  }

  function setOne2MStatus(list) {
    list.forEach(item => {
      const tenantFeatures = item.tenant_features;
      if(tenantFeatures !== undefined && tenantFeatures !== null && tenantFeatures.length > 0)
        for (let i=0; i<tenantFeatures.length; i++) {
          if (tenantFeatures[i].type == 'one2m') {
            item.one2mStatus = tenantFeatures[i].status ? 'Enabled' : 'Disabled';
            break;
          }
        }
    });
  }

  function fetchDetails () {
    AppActions.getAllTenants()
      .then(res => {
        ReactDOM.unstable_batchedUpdates(() => {
          let list = res || [];
          setSptsStatus(list);
          setProxyStatus(list);
          setOne2MStatus(list);
          setList(list);
          setLoading(false);
        });
      })
      .catch(err => {
        ReactDOM.unstable_batchedUpdates(() => {
          showError(err);
          setLoading(false);
        });
      })
      .finally(() => startTimer());
  }

  useEffect(() => fetchDetails(), []);

  return (<>{bodyContent()}</>);
};

export default TenantManagement;
